import * as React from 'react'
import { Link } from 'react-router-dom'
import { FLAGS } from '@atbash-labs/ontology-flags'
import { plantHref } from '@atbash-labs/plantview-links'
import {
  useListSightingsQuery,
  type PlantFaultCauseRef,
  type PlantFaultSymptomRef
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { useFlag } from '../../../components/flags/useFlag'
import { formatRelativeTime } from '../../../lib/time'
import { SectionHeader } from '../entity/SectionHeader'
import { SessionEntityMark } from '../session-mark-ui'

export function FaultKnowledgeSection({
  faults,
  aboutId
}: {
  faults: PlantFaultSymptomRef[]
  aboutId: string
}): React.ReactElement | null {
  if (faults.length === 0) return null

  return (
    <section>
      <SectionHeader
        label="Fault knowledge"
        subtitle={`${faults.length} ${faults.length === 1 ? 'symptom' : 'symptoms'}`}
      />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="flex flex-col gap-3">
          {faults.map((symptom) => (
            <FaultSymptomRow key={symptom.id} symptom={symptom} aboutId={aboutId} />
          ))}
        </div>
      </div>
    </section>
  )
}

function FaultSymptomRow({
  symptom,
  aboutId
}: {
  symptom: PlantFaultSymptomRef
  aboutId: string
}): React.ReactElement {
  const sightingsEnabled = useFlag(FLAGS.RELEASE_SIGHTING_PLANNER)
  const [expanded, setExpanded] = React.useState(false)
  const sightingsQuery = useListSightingsQuery(
    { aboutId, conditionId: symptom.id, limit: 100 },
    { skip: !sightingsEnabled }
  )
  const href = plantHref(symptom.id, 'FaultSymptom', symptom)
  const title = <span className="text-13 font-medium text-ink">{symptom.name}</span>

  return (
    <div className="min-w-0">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {href ? (
              <Link to={href} className="text-ink no-underline hover:text-brand hover:underline">
                {title}
              </Link>
            ) : (
              title
            )}
            {symptom.kind && (
              <Badge variant="secondary" className="text-10">
                {symptom.kind.replace(/_/g, ' ')}
              </Badge>
            )}
            {symptom.severity && (
              <Badge variant="outline" className={severityClass(symptom.severity)}>
                {symptom.severity}
              </Badge>
            )}
            {symptom.exhibits_status && (
              <Badge
                variant="outline"
                className={exhibitsClass(symptom.exhibits_status)}
                title={exhibitsTitle(symptom)}
              >
                {symptom.exhibits_status}
              </Badge>
            )}
            {sightingsEnabled ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
                disabled={sightingsQuery.isLoading}
              >
                {sightingsQuery.data?.length ?? 0}{' '}
                {(sightingsQuery.data?.length ?? 0) === 1 ? 'sighting' : 'sightings'}
              </Button>
            ) : null}
          </div>
        </div>
        <SessionEntityMark entityId={symptom.id} />
      </div>
      {symptom.causes.length > 0 && (
        <div className="mt-1.5 flex flex-col gap-1 border-l border-border pl-3">
          {symptom.causes.map((cause) => (
            <FaultCauseRow key={cause.id} cause={cause} parentEntityId={symptom.id} />
          ))}
        </div>
      )}
      {sightingsEnabled && expanded ? (
        <div className="mt-2 rounded-md border border-line bg-bg-sunk/40 p-2.5">
          {(sightingsQuery.data?.length ?? 0) === 0 ? (
            <p className="m-0 text-12 text-ink-3">No sightings recorded.</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {sightingsQuery.data?.map((sighting) => (
                <li key={sighting.id} className="text-12 text-ink-2">
                  <div>
                    {(sighting.interpretation?.findings ?? [])
                      .map((finding) =>
                        finding.present === undefined
                          ? `${finding.value ?? 'unknown'}${finding.unit ? ` ${finding.unit}` : ''}`
                          : finding.present
                            ? 'Present'
                            : 'Absent'
                      )
                      .join(' · ') ||
                      sighting.note ||
                      'Recorded sighting'}
                  </div>
                  <div className="text-11 text-ink-4">
                    {formatRelativeTime(sighting.observed_at)}
                    {sighting.interpretation
                      ? ` · interpretation ${sighting.interpretation.revision}`
                      : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}

function FaultCauseRow({
  cause,
  parentEntityId
}: {
  cause: PlantFaultCauseRef
  parentEntityId: string
}): React.ReactElement {
  const href = plantHref(cause.id, 'FaultCause', cause)
  const title = <span className="text-13 text-ink-2">{cause.name}</span>
  const pct =
    typeof cause.probability === 'number' && Number.isFinite(cause.probability)
      ? Math.round(Math.max(0, Math.min(1, cause.probability)) * 100)
      : undefined

  return (
    <div className="flex items-start gap-2">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
        {href ? (
          <Link to={href} className="text-ink-2 no-underline hover:text-brand hover:underline">
            {title}
          </Link>
        ) : (
          title
        )}
        {pct !== undefined && (
          <Badge variant="secondary" className="text-10 tabular-nums">
            {pct}%
          </Badge>
        )}
        {typeof cause.confirmations === 'number' && cause.confirmations > 0 && (
          <Badge
            variant="secondary"
            className="text-10 tabular-nums"
            title="Times a reviewer confirmed this cause on a problem with this symptom"
          >
            confirmed ×{cause.confirmations}
          </Badge>
        )}
      </div>
      <SessionEntityMark entityId={cause.id} parentEntityId={parentEntityId} />
    </div>
  )
}

/**
 * `EXHIBITS_SYMPTOM.status` ladder: `observed` is evidence (a trigger fired on
 * this equipment), `monitored` is authored watch, `documented` is prior knowledge.
 */
function exhibitsClass(status: PlantFaultSymptomRef['exhibits_status']): string {
  switch (status) {
    case 'observed':
      return 'border-rose-400/40 bg-rose-500/10 text-10 text-rose-700 dark:text-rose-300'
    case 'monitored':
      return 'border-sky-400/40 bg-sky-500/10 text-10 text-sky-700 dark:text-sky-300'
    default:
      return 'text-10'
  }
}

function exhibitsTitle(symptom: PlantFaultSymptomRef): string {
  switch (symptom.exhibits_status) {
    case 'observed':
      return symptom.last_observed_at
        ? `A trigger for this symptom last fired on this entity ${symptom.last_observed_at}`
        : 'A trigger for this symptom has fired on this entity'
    case 'monitored':
      return 'A trigger watches this entity for this symptom; it has not fired yet'
    default:
      return symptom.exhibits_source
        ? `Documented from ${symptom.exhibits_source.replace(/_/g, ' ')}; not watched by a trigger`
        : 'Documented from ingest or manual knowledge; not watched by a trigger'
  }
}

function severityClass(severity: string): string {
  const normalized = severity.toLowerCase()
  if (normalized === 'high' || normalized === 'critical') {
    return 'border-rose-400/40 bg-rose-500/10 text-10 text-rose-700 dark:text-rose-300'
  }
  if (normalized === 'medium' || normalized === 'moderate') {
    return 'border-amber-400/40 bg-amber-500/10 text-10 text-amber-700 dark:text-amber-300'
  }
  return 'text-10'
}
